import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon, ChevronDownIcon } from '@/components/icons';
import { cn } from '@/utils/cn';

export interface DropdownOption {
  value: string;
  label: string;
}

export interface DropdownProps {
  /** Selected option `value`, or `undefined` for the empty state. */
  value: string | undefined;
  onChange: (value: string) => void;
  options: readonly DropdownOption[];
  /** Trigger label rendered when nothing is selected. */
  placeholder: string;
  /** Header rendered above the option list inside the menu (e.g. "Region"). */
  menuLabel?: string;
  /** Form name for use inside a `<form>` — wired to a hidden input. */
  name?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
  /**
   * Optional icon rendered to the left of the trigger label (e.g. the
   * translate icon for the language picker).
   */
  leadingIcon?: ReactNode;
  /**
   * Trigger size:
   *   - 'lg' (default) — 16px label, p-4, full 1px border. Matches the
   *     contact-form region dropdown.
   *   - 'sm' — 14px semibold label, tighter `pl-3 pr-2.5 py-2` padding,
   *     0.7px border. Matches the footer / mobile-menu language picker.
   */
  size?: 'lg' | 'sm';
  /**
   * When true, the trigger renders without a border or hover/open
   * background — used by the desktop nav language picker. Disabled
   * styling still applies.
   */
  borderless?: boolean;
  /**
   * Extra classes merged into the trigger button (e.g. `py-1.5 px-0`
   * to override default padding). Width should be set on `className`
   * (the wrapper) so the menu sizes to match.
   */
  triggerClassName?: string;
  /**
   * Extra classes merged into the menu's outer (positioning) wrapper.
   * Use to anchor the menu to the right (`left-auto right-0`) and let
   * width hug content for picker-style menus that shouldn't span the
   * trigger width.
   */
  menuWrapperClassName?: string;
  /**
   * Extra classes merged into the menu's inner (chrome) box. Use to
   * swap the bg / shadow / border to match a parent surface — e.g.
   * `border-0 bg-[#edeceb]/70 backdrop-blur-pill shadow-pill` to make
   * the nav language picker's menu read as a continuation of the nav
   * pill itself.
   */
  menuClassName?: string;
  /**
   * Where to open the menu relative to the trigger:
   *   - 'bottom' (default) — menu drops below the trigger.
   *   - 'top'              — menu opens above (drop-up). Used by the
   *                          footer language picker so the list rises
   *                          out of the bottom of the page rather than
   *                          getting clipped by the viewport edge.
   */
  menuPlacement?: 'bottom' | 'top';
  /**
   * If provided, the menu is rendered into this element via React
   * portal instead of as an absolute child of the trigger. Used by the
   * mobile nav menu's language picker so the open menu can read as a
   * second pill below the nav pill (full nav width, frosted bg) rather
   * than as a dropdown floating off the trigger. Outside-click handling
   * still treats the portaled menu as part of the dropdown.
   */
  menuPortalTarget?: HTMLElement | null;
}

/**
 * Form dropdown — figma node 97:796.
 *
 * States:
 *   - Closed (default)            : 1px `clearlab-divider-darker` border,
 *                                   transparent bg, midnight chevron.
 *   - Closed hover                : border ramps to `clearlab-midnight` via
 *                                   `transition-colors duration-fast ease-in`.
 *   - Open                        : midnight border, `clearlab-divider-light/40`
 *                                   bg; menu floats below at 4px gap.
 *   - Selected (closed)           : trigger label shows the picked option's
 *                                   label; styling otherwise default.
 *
 * Menu:
 *   - 1.5px `clearlab-divider-darker` border, `clearlab-oat` bg, `rounded-lg`,
 *     drop shadow.
 *   - Header label (Karla 14/20 steel) with pt-4 / pb-2 / px-4.
 *   - Items: `gap-4` (16px) cluster of a 24px check column + label
 *     (Karla 20/22 midnight). Hover bg `clearlab-divider-light/40`. Selected
 *     items show the check, others render an invisible placeholder so labels
 *     remain aligned.
 *
 * Closes on outside-click and Escape. Hidden `<input>` carries `name`/`required`
 * for native form validation.
 */
export function Dropdown({
  value,
  onChange,
  options,
  placeholder,
  menuLabel,
  name,
  required,
  disabled,
  id,
  className,
  'aria-label': ariaLabel,
  leadingIcon,
  size = 'lg',
  borderless = false,
  triggerClassName,
  menuWrapperClassName,
  menuClassName,
  menuPlacement = 'bottom',
  menuPortalTarget,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const menuId = `${fieldId}-menu`;
  const labelId = menuLabel ? `${fieldId}-menu-label` : undefined;

  useEffect(() => {
    if (!open) return;
    const handlePointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      // Portaled menu lives outside rootRef, so check it separately —
      // otherwise clicking an option in a portal would close the menu
      // before the option's onClick fires.
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // Remove the closed menu (and its option buttons) from the tab order and
  // accessibility tree. Set imperatively via the DOM `inert` property because
  // React 18 doesn't forward the `inert` attribute. Covers the portaled menu
  // too, since `menuRef` points at the rendered node regardless of portal.
  useEffect(() => {
    if (menuRef.current) menuRef.current.inert = !open;
    // `menuPortalTarget` is a dep because the menu node remounts when it moves
    // into a portal target (null → element), and the freshly mounted node needs
    // its inert state (re)applied.
  }, [open, menuPortalTarget]);

  const selected = options.find((opt) => opt.value === value);

  return (
    <div ref={rootRef} className={cn('relative w-full', className)}>
      <button
        type="button"
        id={fieldId}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        // WCAG 2.5.3 (label in name): the accessible name must contain the
        // visible trigger text (the selected option / placeholder). When a
        // contextual label is supplied (e.g. "Change language") we prefix it;
        // otherwise the visible text alone names the button.
        aria-label={
          ariaLabel ? `${ariaLabel}: ${selected ? selected.label : placeholder}` : undefined
        }
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg text-clearlab-midnight',
          // `transition-all` (vs `transition-colors`) so the borderless
          // hover state can also smooth its `rounded-lg → rounded-md`
          // tween alongside the bg-color fade.
          'transition-all duration-fast ease-in-out',
          // The midnight border IS the focus indicator (when bordered) — suppress
          // the global `:focus-visible` blue ring so it doesn't stack on top.
          'focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
          // Size — font + default padding
          size === 'lg' && 'px-4 py-4 text-[16px] font-normal leading-[18px]',
          size === 'sm' && 'py-2 pl-3 pr-2.5 text-label-14 font-semibold leading-[16px]',
          // Border + state styling — outlined gets feedback; borderless gets a
          // tinted "pill" treatment on hover (8px corners, divider-light/40 bg)
          // matching the nav-language-picker spec.
          !borderless && size === 'lg' && 'border',
          !borderless && size === 'sm' && 'border-[0.7px]',
          !borderless &&
            (open
              ? 'border-clearlab-midnight bg-clearlab-divider-light/40'
              : 'border-clearlab-divider-darker bg-transparent hover:border-clearlab-midnight focus:border-clearlab-midnight'),
          borderless && 'hover:rounded-md hover:bg-clearlab-divider-light/40',
          disabled && 'cursor-not-allowed opacity-60 hover:border-clearlab-divider-darker',
          // Consumer overrides — width should live on the wrapper (className)
          // so the menu matches; this slot is for trigger-specific tweaks
          // like padding.
          triggerClassName,
        )}
      >
        <span className="flex min-w-0 items-center gap-1">
          {leadingIcon}
          <span className="truncate">{selected ? selected.label : placeholder}</span>
        </span>
        <ChevronDownIcon
          className={cn(
            'size-4 shrink-0 text-clearlab-midnight',
            // Flip chevron 180° while the menu is open so it reads as an
            // up-chevron, then rotate back on close.
            'transition-transform duration-300 ease-in-out',
            open && 'rotate-180',
          )}
        />
      </button>

      {/* Hidden input so the dropdown participates in native form submission /
          required-field validation alongside `<input>`-based fields. */}
      {name ? (
        <input
          type="hidden"
          name={name}
          value={value ?? ''}
          required={required}
          aria-hidden
          tabIndex={-1}
        />
      ) : null}

      {/* Menu — always rendered so open/close has both ends to interpolate.
          Two reveal mechanisms by placement:
          • Drop-down: outer `grid` animates its single row `0fr → 1fr`,
            so the wrapper itself grows downward from the trigger's bottom
            edge. The CLIP layer (`overflow-hidden`) hides everything
            below the wrapper, and the listbox in normal flow reveals
            top → bottom as the row expands.
          • Drop-up:   the wrapper sits at the listbox's natural height
            above the trigger, and `clip-path: inset(...)` shrinks the
            top inset from 100% to 0%. The bottom edge stays anchored at
            the trigger's top edge throughout, and content reveals
            bottom → top (last row first, then second-to-last, …, first
            row last). Grid-row animation can't express this cleanly:
            flex-shrink interactions and intrinsic-size quirks let the
            inner content overflow in the wrong direction. Inset-clip
            doesn't touch layout at all, so the reveal direction is
            unambiguous.

          When `menuPortalTarget` is provided the menu is rendered into
          that element instead of as an absolute child of the trigger;
          in that case the absolute-positioning + offset classes are
          dropped (the target controls its own placement). */}
      {(() => {
        const listboxBox = (
          <div
            id={menuId}
            role="listbox"
            aria-labelledby={labelId}
            className={cn(
              'flex flex-col items-start rounded-lg border-[1.5px] border-clearlab-divider-darker',
              'bg-clearlab-oat shadow-[1px_1px_6px_rgba(34,34,33,0.15)]',
              // Consumer override for chrome (e.g. nav-style frosted bg).
              menuClassName,
            )}
          >
            {menuLabel ? (
              <p
                id={labelId}
                className="px-4 pb-2 pt-4 text-[14px] font-normal leading-5 text-clearlab-steel"
              >
                {menuLabel}
              </p>
            ) : null}
            <ul className="flex w-full flex-col gap-1 p-2">
              {options.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <li key={opt.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => {
                        onChange(opt.value);
                        setOpen(false);
                      }}
                      className={cn(
                        'flex w-full items-center gap-4 rounded-lg p-4 text-left',
                        'text-[16px] font-normal leading-[18px] text-clearlab-midnight',
                        'transition-colors duration-fast ease-in-out',
                        'hover:bg-clearlab-divider-light/40 focus:bg-clearlab-divider-light/40 focus:outline-none',
                      )}
                    >
                      <CheckIcon
                        className={cn(
                          'size-5 shrink-0 text-clearlab-midnight',
                          isSelected ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="flex-1 truncate">{opt.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        );

        // Asymmetric timing: opening at 300ms feels responsive on click;
        // closing at 500ms gives the unwind enough room to settle.
        const durationClass = open ? 'duration-300' : 'duration-500';
        const pointerClass = open ? 'pointer-events-auto' : 'pointer-events-none';

        const menuTree =
          menuPlacement === 'top' ? (
            <div
              ref={menuRef}
              className={cn(
                !menuPortalTarget && 'absolute bottom-full left-0 right-0 z-20 mb-1',
                'transition-[clip-path,opacity] ease-in-out',
                durationClass,
                pointerClass,
                menuWrapperClassName,
              )}
              style={{
                // inset(top right bottom left). Closed: top inset = 100%
                // (entire menu hidden by clipping from the top down to the
                // bottom edge). Open: top inset = 0% (full menu visible).
                // As the top inset shrinks from 100% → 0%, the visible
                // slice grows from a 0-tall sliver at the bottom upward —
                // exactly the bottom-up reveal anchored at the trigger.
                clipPath: open ? 'inset(0 0 0 0)' : 'inset(100% 0 0 0)',
                opacity: open ? 1 : 0,
              }}
              aria-hidden={!open}
            >
              {listboxBox}
            </div>
          ) : (
            <div
              ref={menuRef}
              className={cn(
                'grid',
                !menuPortalTarget && 'absolute left-0 right-0 top-full z-20 mt-1',
                'transition-[grid-template-rows] ease-in-out',
                durationClass,
                pointerClass,
                menuWrapperClassName,
              )}
              style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
              aria-hidden={!open}
            >
              <div className="overflow-hidden rounded-lg">
                <div
                  className={cn(
                    'transition-opacity ease-in-out',
                    open ? 'opacity-100 duration-300' : 'opacity-0 duration-500',
                  )}
                >
                  {listboxBox}
                </div>
              </div>
            </div>
          );
        return menuPortalTarget ? createPortal(menuTree, menuPortalTarget) : menuTree;
      })()}
    </div>
  );
}
